import PropTypes from 'prop-types'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { ReplyButton } from './ReplyButton'
import { EditingActionButtons, ReplyingActionButtons } from './EditingControls'
import { SupplierContext } from 'components/supplier_show/SupplierContext'
import { IconVariant, SupplierIcon } from 'components/supplier_shared/supplier_icon'
import ContentEditable, { ContentEditableEvent } from 'react-contenteditable'

interface SupplierSignatureProps {
  updatedAt?: Date
}

function SupplierSignature ({ updatedAt }: SupplierSignatureProps): React.ReactElement {
  const { supplierId, supplierName, logoUrl } = useContext(SupplierContext)
  const displayDate = (updatedAt != null) ? new Intl.DateTimeFormat('en-US').format(updatedAt) : 'N/A'
  const supplierIconId = supplierId ?? 0
  return (
    <div className='review-reply-supplier-signature'>
      <SupplierIcon variant={IconVariant.XSMALL} id={supplierIconId} name={supplierName ?? ''} logoUrl={logoUrl} />
      <div className='signature-supplier-name'>{supplierName}</div>
      <span className='flex-child-grow' />
      <div className='signature-supplier-date pro-text-xs'>Last&nbsp;Updated&nbsp;{displayDate}</div>
    </div>
  )
}

interface TextReplyProps {
  replyText: string
}

export function FullReply ({ replyText }: TextReplyProps): React.ReactElement {
  return (
    <div className='display'>
      {replyText}
    </div>
  )
}

interface CondensedReplyProps {
  condensedText: string
  expandLabel: string
  onExpand: () => void
}

export function CondensedReply ({ condensedText, expandLabel, onExpand }: CondensedReplyProps): React.ReactElement {
  return (
    <div className='display'>
      <span>
        {`${condensedText}...`}&nbsp;
        <button className='review-reply-read-more' onClick={onExpand}>{expandLabel}</button>
      </span>
    </div>
  )
}

function calculateCutoffLimit (maxChars: number, text: string): number {
  const newLineCutoff = text.indexOf('\n')
  // cut off text if there is a newLine
  return (text.length < maxChars && newLineCutoff > 0) ? newLineCutoff : maxChars
}

interface DisplayReplyProps {
  replyText: string
  updatedAt?: Date
}

export function DisplayReply ({ replyText, updatedAt }: DisplayReplyProps): React.ReactElement {
  const [condensedVersion, setCondensedVersion] = useState(true)

  // maxCharCount was determined empirically counting characters to fill two lines
  // in the desktop browser. This corresponds to about 4 lines on mobile
  const maxCharCount = 200
  const cutOff = calculateCutoffLimit(maxCharCount, replyText)

  let displayText = replyText
  let replyElement
  if (replyText.length > cutOff && condensedVersion) {
    displayText = replyText.slice(0, cutOff)
    replyElement = (
      <CondensedReply
        condensedText={displayText}
        expandLabel='Read More'
        onExpand={() => (setCondensedVersion(false))}
      />
    )
  } else {
    replyElement = <FullReply replyText={replyText} />
  }

  return (
    <div className='review-reply-textarea-container'>
      {replyElement}
      <SupplierSignature updatedAt={updatedAt} />
    </div>
  )
}

interface EditReplyFormProps {
  initialText: string
  onCancel: () => void
  onDelete: () => void
  onPost: (arg0: string) => void
}

function EditReplyForm ({ initialText, onCancel, onDelete, onPost }: EditReplyFormProps): React.ReactElement {
  const [text, setText] = useState(initialText)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef?.current?.focus()
  }, [inputRef])

  const handleChange = (event: ContentEditableEvent): void => {
    const targetElement = event?.nativeEvent?.target
    if (targetElement instanceof HTMLElement) {
      const userText = targetElement.innerText ?? event?.target?.value
      if (userText == null) { return }
      setText(userText)
    }
  }

  const handlePost = (): void => onPost(text)

  return (
    <div className='review-reply-textarea-container'>
      <ContentEditable
        innerRef={inputRef}
        className='review-reply-textarea'
        role='textbox'
        aria-multiline
        onChange={handleChange}
        html={text}
      />
      <EditingActionButtons onCancel={onCancel} onDelete={onDelete} onPost={handlePost} />
    </div>
  )
}

interface ReadyToEditProps {
  initialText: string
  updatedAt?: Date
  onClick: () => void
}

export function ReadyToEdit ({ initialText, updatedAt, onClick }: ReadyToEditProps): React.ReactElement {
  return (
    <>
      <div className='reply-button-container margin-top-1'>
        <ReplyButton onClick={onClick}>
          Edit
        </ReplyButton>
      </div>
      <DisplayReply replyText={initialText} updatedAt={updatedAt} />
    </>
  )
}

export function Editing ({ initialText, reviewId, onCancel, onDelete, onPost }: any): React.ReactElement {
  const onEditPost = (replyText: string): void => {
    onPost(replyText, reviewId)
  }

  return (
    <>
      <div className='reply-button-container margin-top-1'>
        <ReplyButton disabled>
          Edit
        </ReplyButton>
      </div>
      <EditReplyForm
        initialText={initialText}
        onCancel={onCancel}
        onDelete={onDelete}
        onPost={onEditPost}
      />
    </>
  )
}

interface ReadyToReplyProps {
  onClick: () => void
}

export function ReadyToReply ({ onClick }: ReadyToReplyProps): React.ReactElement {
  return (
    <>
      <div className='reply-button-container margin-top-1'>
        <ReplyButton onClick={onClick}>
          Reply
        </ReplyButton>
      </div>
    </>
  )
}

interface ReplyingProps {
  onCancel: () => void
  onPost: (arg0: string) => void
}

function NewReplyForm ({ onCancel, onPost }: ReplyingProps): React.ReactElement {
  const [text, setText] = useState<string>('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef?.current?.focus()
  }, [inputRef])

  const handleChange = (event: ContentEditableEvent): void => {
    const targetElement = event?.nativeEvent?.target
    if (targetElement instanceof HTMLElement) {
      const userText = targetElement.innerText ?? event?.target?.value
      if (userText == null) { return }
      setText(userText)
    }
  }

  const handlePost = (): void => {
    onPost(text)
  }

  return (
    <div className='review-reply-textarea-container'>
      <ContentEditable
        innerRef={inputRef}
        className='review-reply-textarea'
        role='textbox'
        aria-multiline
        onChange={handleChange}
        html={text}
      />
      <ReplyingActionButtons onCancel={onCancel} onPost={handlePost} />
    </div>
  )
}

export function Replying ({ onPost, onCancel }: ReplyingProps): React.ReactElement {
  return (
    <>
      <div className='reply-button-container margin-top-1'>
        <ReplyButton disabled>
          Reply
        </ReplyButton>
      </div>
      <NewReplyForm onCancel={onCancel} onPost={onPost} />
    </>
  )
}

Replying.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onPost: PropTypes.func.isRequired
}

NewReplyForm.propTypes = Replying.propTypes
